/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useEffect, useRef, useState } from 'react';
import type {
  FieldValueSuggestion,
  PropertySuggestableField,
} from '@services/requests/generated/types.gen';
import { getPropertyFieldValues } from '@services/requests/housien-services/PropertyFieldValuesService';

/** Letters typed before the agency's values are looked up (plan §4.1). */
export const MIN_QUERY_LENGTH = 2;

/** Pause after the last keystroke before asking the server. */
export const SUGGESTION_DEBOUNCE_MS = 250;

/** Items asked for per lookup. */
export const SUGGESTION_LIMIT = 10;

/**
 * Whether a typed text is long enough to look up.
 *
 * @param query - The text in the input.
 * @returns True from two non-blank characters on.
 */
export function isSearchableQuery(query: string): boolean {
  return query.trim().length >= MIN_QUERY_LENGTH;
}

interface FieldValueSuggestionsState {
  items: FieldValueSuggestion[];
  loading: boolean;
}

const EMPTY: FieldValueSuggestionsState = { items: [], loading: false };

/**
 * Whether the list is open on a query worth sending.
 *
 * @param enabled - Whether the list is open.
 * @param agencyId - The agency, empty before it loads.
 * @param query - The typed text.
 * @returns True when a lookup should run.
 */
function shouldLookUp(enabled: boolean, agencyId: string, query: string): boolean {
  return enabled && Boolean(agencyId) && isSearchableQuery(query);
}

/**
 * Agency values for one property field that contain the typed text,
 * debounced, with stale answers discarded.
 *
 * @param agencyId - The agency whose listings are read.
 * @param field - The suggestable field.
 * @param query - The text typed so far.
 * @param enabled - Only looks up while the list is open.
 * @returns The suggestions and whether a lookup is in flight.
 */
export function useFieldValueSuggestions(
  agencyId: string,
  field: PropertySuggestableField,
  query: string,
  enabled: boolean,
): FieldValueSuggestionsState {
  const [state, setState] = useState<FieldValueSuggestionsState>({ items: [], loading: false });
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    if (!shouldLookUp(enabled, agencyId, query)) {
      setState(EMPTY);
      return undefined;
    }
    /** Applies an answer only while it is still the latest request. */
    const settle = (next: FieldValueSuggestionsState) => {
      if (id === requestId.current) setState(next);
    };
    setState((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(() => {
      getPropertyFieldValues(agencyId, field, query.trim(), SUGGESTION_LIMIT)
        .then((res) => settle({ items: res.values ?? [], loading: false }))
        .catch(() => settle(EMPTY));
    }, SUGGESTION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [agencyId, field, query, enabled]);

  return state;
}
